import { ascBy, lowerCaseProperty } from 'app/helpers/object'

/**
 * Refresh SmartTable event listener
 * {@link https://github.com/lorenzofox3/Smart-Table/issues/363#issuecomment-246636293}
 *
 * @class refreshTable
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:refreshTable
 */
/* @ngInject */
export function refreshTable () {
  return {
    require: 'stTable',
    restrict: 'A',
    link: function (scope, _elem, _attr, table) {
      scope.$on('refreshTable', function (event, args = {}) {
        const matchesId = !_attr.id || !args.id || _attr.id === args.id
        if (!matchesId) return

        if (args.resetState) {
          const ts = table.tableState()
          ts.sort = {}
          ts.search = { predicateObject: {} }
          ts.pagination.start = 0
        }

        table.pipe()
      })
    }
  }
}

/**
 * Highlight SmartTable items,
 * expose search to scope
 *
 * @class highlightTable
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:highlightTable
 */
/* @ngInject */
export function highlightTable () {
  return {
    require: 'stTable',
    restrict: 'A',
    link: function (scope, _elem, _attr, table) {
      // watch table state
      scope.$watch(() => table.tableState().search, function (value) {
        scope.ht = value.predicateObject
      }, true)
    }
  }
}

/**
 * Page selector for SmartTable
 * {@link http://embed.plnkr.co/LB2f9P}
 *
 * @class pageSelect
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:pageSelect
 *
 * @example
 * <page-select></page-select>
 */
/* @ngInject */
export function pageSelect () {
  return {
    restrict: 'E',
    require: '^stTable',
    template: /* html */ `
      <span class="select-page">
        <input type="number" ng-model="page.num" ng-if="true || allpages.length >= 80 || type === 'ER'" style="width: 100%; text-align: center;" ng-change="selectPage(page.num)" ng-model-options="{ allowInvalid: false, debounce: 1000 }">
        <ui-select theme="select2" ng-model="page.num" on-select="selectPage(page.num)" ng-if="false && allpages.length < 80 && !type">
          <ui-select-match placeholder="{{page.num}}">
            <span ng-bind="page.num"></span>
          </ui-select-match>
          <ui-select-choices repeat="p in allpages | filter:$select.search">
            <span value="{{p}}" ng-bind="p"></span>
          </ui-select-choices>
        </ui-select>
      </span>`,
    link: function (scope, _element, attrs) {
      const min = attrs.min ? parseInt(attrs.min, 10) : 1
      if (scope.$parent && scope.$parent.$parent && scope.$parent.$parent.$parent.$parent &&
        scope.$parent.$parent.$parent.$parent.$parent && scope.$parent.$parent.$parent.$parent.$parent.$parent) {
        scope.type = scope.$parent.$parent.$parent.$parent.$parent.$parent.loadingER ? 'ER' : null
      } else {
        scope.type = null
      }
      scope.page = {}
      scope.$watch('currentPage', function (c) {
        scope.page.num = c
      })

      scope.$watch('page.num', function (c) {
        if (scope.allpages && c > scope.allpages.length) {
          scope.page.num = scope.currentPage
        }
      })

      scope.$watch('numPages', function (n) {
        const max = n ? parseInt(n, 10) : 1
        scope.allpages = []
        for (let i = min; i <= max; i++) {
          scope.allpages.push(i)
        }
      })
    }
  }
}

/**
 * Page selector for SmartTable
 * {@link http://embed.plnkr.co/LB2f9P}
 *
 * @class pageSize
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:pageSize
 *
 * @example
 * <page-size></page-size>
 */
/* @ngInject */
export function pageSize () {
  return {
    restrict: 'E',
    require: '^stTable',
    template: /* html */ `
      <button ng-if="!type" type="button" class="btn btn_sorting-button"
        ng-repeat="count in [5, 25, 50, 100]"
        ng-class="{active: stItemsByPage === count}"
        ng-click="setPageSize(count)" ng-bind="count"></button>
      <button ng-if="type ==='TMDB'" type="button" class="btn btn_sorting-button active"
        ng-bind="'20'"></button>`,
    link: function (scope) {
      if (Object.prototype.hasOwnProperty.call(scope.$parent, 'searchForm') || (scope.$parent.$parent && Object.prototype.hasOwnProperty.call(scope.$parent.$parent, 'searchForm'))) {
        scope.type = 'TMDB'
      } else {
        scope.type = null
      }
      scope.setPageSize = count => {
        scope.stItemsByPage = count
      }
    }
  }
}

/**
 * Multiple select filter for SmartTable
 * {@link http://embed.plnkr.co/SOcUk1}
 *
 * @class stSelectMultiple
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:stSelectMultiple
 *
 * @example
 * <st-select-multiple list="collection" predicate="education" placeholder="Select..."></st-select-multiple>
 */
/* @ngInject */
export function stSelectMultiple () {
  return {
    restrict: 'E',
    require: '^stTable',
    scope: {
      list: '=',
      predicate: '@',
      placeholder: '@',
      type: '@?',
      labels: '=?'
    },
    template: /* html */ `
      <div class="modded" ng-class="{{class}}">
        <ui-select multiple theme="bootstrap" close-on-select="{{closeOnSelect}}" title="{{placeholder}}"
                  ng-model="selected.items" ng-change="change(selected.items)">
          <ui-select-match placeholder="{{placeholder}}">{{type === 'array' ? (labels ? labels[$item] : $item) : $item.value}}</ui-select-match>
          <ui-select-choices repeat="item in collection | filter:$select.search">
            <span>{{type === 'array' ? (labels ? labels[item] : item) : item.value}}</span>
          </ui-select-choices>
        </ui-select>
      </div>`,
    link: function (scope, _element, attr, table) {
      scope.closeOnSelect = attr.closeOnSelect ? attr.closeOnSelect : 'true'
      scope.class = attr.class ? attr.class : 'filters'
      scope.selected = { items: [] }

      // watch given list
      scope.$watch('list', function (list) {
        if (scope.type === 'array') {
          scope.collection = scope.list
        } else if (typeof list === 'object') {
          scope.collection = Array.isArray(list)
            ? list.map(i => typeof i === 'object' ? i : { key: i, value: i }) // from array
            : Object.keys(list).map(k => ({ key: k, value: list[k] })) // from object
          scope.collection.sort(ascBy(lowerCaseProperty('value')))
        }
      }, true)

      // change selected items -> call table search
      scope.change = function (items) {
        if (scope.type !== 'array') {
          table.search(items && items.length ? items.map(i => i.key) : null, scope.predicate)
        } else {
          table.search(items && items.length ? items : null, scope.predicate)
        }
      }

      // watch table state
      scope.$watch(() => table.tableState().search, function (value) {
        const selected = value.predicateObject && value.predicateObject[scope.predicate]
        const hasSelection = Array.isArray(selected) && selected.length > 0
        if (hasSelection) {
          scope.selected.items = scope.collection.filter(i => value.predicateObject[scope.predicate].indexOf(scope.type === 'array' ? i : i.key) > -1)
        } else {
          scope.selected.items = []
        }
      }, true)

      // Forward clicks on the .modded wrapper (including the ::after chevron area) to the
      // ui-select search input. The ::after has pointer-events:none so clicks land on the
      // .modded container div, which has no ng-click; we bridge the gap here.
      var moddedEl = _element[0].querySelector('.modded')
      if (moddedEl) {
        angular.element(moddedEl).on('click', function (e) {
          var input = moddedEl.querySelector('input.ui-select-search')
          if (input && e.target !== input) {
            angular.element(input).triggerHandler('click')
          }
        })
      }
    }
  }
}

/**
 * Single select filter for SmartTable
 * {@link http://embed.plnkr.co/SOcUk1}
 *
 * @class stSelect
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:stSelect
 *
 * @example
 * <st-select list="collection" predicate="education" placeholder="Select..."></st-select>
 */
/* @ngInject */
export function stSelect () {
  return {
    restrict: 'E',
    require: '^stTable',
    scope: {
      list: '=',
      predicate: '@',
      placeholder: '@',
      onRefresh: '=',
      onScroll: '='
    },
    template: /* html */ `
      <div class="modded" ng-class="{{class}}">
        <ui-select theme="select2" close-on-select="true" title="{{placeholder}}"
                spinner-enabled="true"
                ng-model="selected.item" ng-change="change(selected.item)">
          <ui-select-match allow-clear="true" placeholder="{{placeholder}}">
            <span class="ui-select-option">{{selected.item.value}}</span>
          </ui-select-match>
          <ui-select-choices repeat="item in collection | filter: filterChoices($select.search)"
                    refresh="onRefresh($select)" scroll="onScroll()">
            <span ng-bind="item.value" class="ui-select-option"></span> 
          </ui-select-choices>
        </ui-select>
      </div>`,
    link: function (scope, _element, attr, table) {
      scope.filterChoices = function (param) {
        return scope.onRefresh ? '' : param
      }

      scope.class = attr.class ? attr.class : 'filters'
      scope.selected = { item: null }

      // watch given list
      scope.$watch('list', function (list) {
        if (typeof list === 'object') {
          scope.collection = Array.isArray(list)
            ? list.map(i => typeof i === 'object' ? i : { key: i, value: i }) // from array
            : Object.keys(list).map(k => ({ key: k, value: list[k] })) // from object
          scope.collection.sort(ascBy(lowerCaseProperty('value')))
        }
      }, true)

      // change selected item -> call table search
      scope.change = function (item) {
        table.search(item ? item.key : null, scope.predicate)
      }

      // watch table state
      scope.$watch(() => table.tableState().search, function (value) {
        if (scope.collection && scope.collection.length && value.predicateObject && value.predicateObject[scope.predicate]) {
          scope.selected.item = scope.collection.find(i => i.key === value.predicateObject[scope.predicate])
        }
      }, true)
    }
  }
}

/**
 * Dates filter for SmartTable
 *
 * @class stDates
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:stDates
 *
 * @example
 * <st-dates predicate="lastRun"></st-dates>
 */
/* @ngInject */
export function stDates () {
  return {
    restrict: 'E',
    require: '^stTable',
    scope: {
      predicate: '@'
    },
    template: /* html */ `
      <div class="calendarstyle">
        After:
        <p class="input-group">
          <input type="text" class="form-control" ng-model="value.after" ng-model-options="{allowInvalid:false}"
                uib-datepicker-popup="dd.MM.yyyy" is-open="opened.after" close-text="Close" ng-change="change()" />
          <span class="input-group-btn">
            <button type="button" class="btn btn-default btn-half" ng-click="open('after')">
              <i class="glyphicon glyphicon-calendar"></i>
            </button>
          </span>
        </p>
        Before:
        <p class="input-group">
          <input type="text" class="form-control" ng-model="value.before" ng-model-options="{allowInvalid:false}"
                uib-datepicker-popup="dd.MM.yyyy" is-open="opened.before" close-text="Close" ng-change="change()" />
          <span class="input-group-btn">
            <button type="button" class="btn btn-default btn-half" ng-click="open('before')">
              <i class="glyphicon glyphicon-calendar"></i>
            </button>
          </span>
        </p>
      </div>`,
    link: function (scope, _element, _attr, table) {
      scope.value = { after: null, before: null }
      scope.opened = { after: false, before: false }
      scope.open = w => { scope.opened[w] = true }
      scope.change = function () {
        var filter = scope.value.after || scope.value.before ? {} : null
        if (scope.value.after) {
          filter.after = scope.value.after
        }
        if (scope.value.before) {
          filter.before = scope.value.before
          let end = new Date(scope.value.before).setHours(23)
          end = new Date(end).setMinutes(59)
          end = new Date(end).setSeconds(59)
          end = new Date(end).setMilliseconds(999)
          filter.before = new Date(end)
        }

        // hack, we need to pass filter object like string,
        // because in the other case $filter service will travel through actual object
        // e.g. filter like {higher:10} will get 'higher' property from each number, and compare it against 10
        // but we need special logic for object filters, for dates and numbers
        table.search(filter ? JSON.stringify(filter) : null, scope.predicate)
      }
    }
  }
}

/**
 * Numbers filter for SmartTable
 *
 * @class stNumbers
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:stNumbers
 *
 * @example
 * <st-numbers predicate="lastRun" min="2"></st-numbers>
 */
/* @ngInject */
export function stNumbers () {
  return {
    restrict: 'E',
    require: '^stTable',
    scope: {
      predicate: '@',
      min: '=?'
    },
    template: /* html */ `
      <div>
        <b>Start</b>: <input type="number" class="form-control st-number-input" ng-model="value.higher" ng-change="change()" min="{{min}}" ng-model-options="{debounce:700}"/>
        <b>End</b>: <input type="number" class="form-control st-number-input" ng-model="value.lower" ng-change="change()" min="{{min}}" ng-model-options="{debounce:700}" />
      </div>`,
    link: function (scope, _element, _attr, table) {
      scope.value = { higher: null, lower: null }
      scope.change = function () {
        var isHigher = typeof scope.value.higher === 'number'
        var isLower = typeof scope.value.lower === 'number'
        var filter = isHigher || isLower ? {} : null
        if (isHigher) {
          filter.higher = scope.value.higher
        }
        if (isLower) {
          filter.lower = scope.value.lower
        }

        // hack, we need to pass filter object like string,
        // because in the other case $filter service will travel through actual object
        // e.g. filter like {higher:10} will get 'higher' property from each number, and compare it against 10
        // but we need special logic for object filters, for dates and numbers
        table.search(filter ? JSON.stringify(filter) : null, scope.predicate)
      }
    }
  }
}

/**
 * Add class for predicate in SmartTable
 *
 * @class stSearchClassBy
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:stSearchClassBy
 *
 * @example
 * <i class="fa fa-filter" st-search-class-by="firstname" uib-dropdown-toggle></i>
 */
/* @ngInject */
export function stSearchClassBy ($parse) {
  return {
    restrict: 'A',
    require: '^stTable',
    link: function (scope, element, attr, table) {
      // watch table state
      scope.$watch(() => table.tableState().search, function (value) {
        if (value.predicateObject && $parse(attr.stSearchClassBy || '$')(value.predicateObject)) {
          element.addClass('filtered')
        } else {
          element.removeClass('filtered')
        }
      }, true)
    }
  }
}

/**
 * Table column filter dropdown wrapper for SmartTable.
 * Encapsulates the uib-dropdown toggle pattern used in every filterable <th>.
 * Transclude the filter content (st-select-multiple, input[st-search], etc.).
 *
 * Sets the `filtered` CSS class on the filter icon when the predicate has an
 * active value in the SmartTable search state.
 *
 * @param {string} predicate - The tableState search key this filter controls
 *   (e.g. "status", "systems"). Must match the predicate used by the
 *   transcluded st-search / st-select-multiple child.
 *
 * Note: the template contains `on-toggle="toggled(open)"` — `toggled` is not
 * defined on the isolated scope, so this binding is a silent no-op. uib-dropdown
 * calls it harmlessly on open/close.
 *
 * @example
 * <st-table-filter predicate="systems">
 *   <st-select-multiple list="vm.types" predicate="systems" placeholder="Select..."></st-select-multiple>
 * </st-table-filter>
 */
/* @ngInject */
export function stTableFilter () {
  return {
    restrict: 'E',
    require: '^stTable',
    transclude: true,
    scope: {
      predicate: '@'
    },
    template: /* html */ `
      <span uib-dropdown on-toggle="toggled(open)" ng-click="stopEvent($event)">
        <i class="fa fa-filter" uib-dropdown-toggle ng-class="{filtered: isFiltered}"></i>
        <div class="dropdown-menu" ng-transclude></div>
      </span>`,
    link: function (scope, _element, _attr, table) {
      scope.stopEvent = function (e) { e.stopPropagation() }

      scope.$watch(() => table.tableState().search, function (value) {
        const pred = value.predicateObject && value.predicateObject[scope.predicate]
        scope.isFiltered = !!pred && (!Array.isArray(pred) || pred.length > 0)
      }, true)
    }
  }
}
